import { useState } from "react";
import { Ban, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TooltipButton } from "@/components/common/Tooltips";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Cancelación LÓGICA de un pago. Visible y funcional SOLO para el administrador de la entidad.
// El backend (POST /pagos/{id}/cancelar) recalcula la tabla (replay) y conserva el dato (51ª).
export function CancelarPagoButton({ pago, onDone, size = "sm" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role !== "admin_entidad") return null;
  if (pago?.estatus === "Cancelado")
    return <span className="text-[11px] font-medium text-red-500" data-testid={`pago-cancelado-${pago.id}`}>Cancelado</span>;

  const cancelar = async () => {
    const m = motivo.trim();
    if (m.length < 4) { toast.error("Escribe el motivo de la cancelación (mínimo 4 caracteres)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/pagos/${pago.id}/cancelar`, { motivo: m });
      toast.success(data?.credito_recomputado
        ? "Pago cancelado. La tabla de amortización se recalculó."
        : "Pago cancelado (dato conservado).");
      setOpen(false);
      setMotivo("");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cancelar el pago");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TooltipButton
        tooltip="Cancelar este pago (solo administrador; queda en bitácora)"
        data-testid={`cancelar-pago-btn-${pago.id}`}
        variant="ghost"
        size={size === "icon" ? "icon" : "sm"}
        className="h-8 gap-1.5 px-2.5 text-red-600 hover:bg-red-50 hover:text-red-700"
        onClick={() => setOpen(true)}
      >
        <Ban className="h-4 w-4" /> {size !== "icon" && "Cancelar"}
      </TooltipButton>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="cancelar-pago-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar pago {pago?.recibo}</AlertDialogTitle>
            <AlertDialogDescription>
              La cancelación es lógica: el pago se conserva como evidencia (10 años, 51ª LFPIORPI) y
              queda en bitácora. La tabla de amortización y la mora se recalculan reaplicando los pagos
              vigentes. Esta acción no borra el registro.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label className="mb-1 block text-xs">Motivo de la cancelación <span className="text-red-500">*</span></Label>
            <Textarea
              data-testid="cancelar-pago-motivo"
              rows={2}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej. pago duplicado, error de captura, devolución..."
            />
            <p className="mt-1 text-[11px] text-slate-400">Obligatorio. Quedará en bitácora y en el recibo.</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Volver</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirmar-cancelar-pago"
              onClick={(e) => { e.preventDefault(); cancelar(); }}
              disabled={busy || motivo.trim().length < 4}
              className="bg-red-600 hover:bg-red-700"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Confirmar cancelación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
